import { useMemo } from 'react';
import { darkTheme } from '../../../lib/tokens';
import type { DailyHeatmapEntry } from './api';

interface Props {
  daily: DailyHeatmapEntry[];
}

// GitHub-Style 90-Tage-Heatmap. Spalten = Wochen, Zeilen = Wochentage (Mo→So).
// Intensitaet aus completedMinutes / max(plannedMinutes, 1) — geclamped auf 0..1.
// Power-Sessions kriegen einen extra Akzent. Tooltip via title-Attribute.

const CELL_SIZE = 12;
const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

interface HeatmapCell {
  date: string;
  intensity: number; // 0..1
  isLearningDay: boolean;
  hadPowerSession: boolean;
  completedMinutes: number;
  plannedMinutes: number;
  streakDayNumber: number;
  empty?: boolean;
}

export function EngagementHeatmap({ daily }: Props): JSX.Element {
  const { weeks, totalDays, activeDays, totalMinutes } = useMemo(() => {
    return buildWeeks(daily);
  }, [daily]);

  if (daily.length === 0) {
    return (
      <SectionCard title="Engagement (90 Tage)">
        <p className="text-xs text-text-tertiary">Noch keine Lerntage erfasst.</p>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Engagement (90 Tage)"
      hint={`${activeDays}/${totalDays} aktive Tage · ${Math.round(totalMinutes)} min gesamt`}
    >
      <div className="flex gap-2">
        <div className="flex flex-col gap-[3px] pt-[2px] text-[9px] text-text-tertiary">
          {DAY_LABELS.map((d, i) => (
            <div key={d} style={{ height: CELL_SIZE, lineHeight: `${CELL_SIZE}px` }}>
              {i % 2 === 0 ? d : ''}
            </div>
          ))}
        </div>
        <div className="flex gap-[3px] overflow-x-auto pb-1">
          {weeks.map((week, wi) => (
            <div key={wi} className="flex flex-col gap-[3px]">
              {week.map((cell, ci) => (
                <Cell key={ci} cell={cell} />
              ))}
            </div>
          ))}
        </div>
      </div>
      <Legend />
    </SectionCard>
  );
}

function Cell({ cell }: { cell: HeatmapCell }): JSX.Element {
  if (cell.empty) {
    return (
      <div
        style={{
          width: CELL_SIZE,
          height: CELL_SIZE,
          background: 'transparent',
        }}
      />
    );
  }

  const bg = cellColor(cell);
  const ring = cell.hadPowerSession ? `inset 0 0 0 1.5px ${darkTheme.colors.warning}` : 'none';
  const tooltip = cellTooltip(cell);

  return (
    <div
      title={tooltip}
      style={{
        width: CELL_SIZE,
        height: CELL_SIZE,
        background: bg,
        borderRadius: 2,
        boxShadow: ring,
        opacity: cell.isLearningDay ? 1 : 0.45,
      }}
      aria-label={tooltip}
    />
  );
}

function Legend(): JSX.Element {
  return (
    <div className="mt-3 flex items-center gap-3 text-[10px] text-text-tertiary">
      <span>weniger</span>
      {[0, 0.25, 0.5, 0.75, 1].map((i) => (
        <div
          key={i}
          style={{
            width: CELL_SIZE,
            height: CELL_SIZE,
            background: scaleColor(i),
            borderRadius: 2,
          }}
        />
      ))}
      <span>mehr</span>
      <span className="ml-3 flex items-center gap-1">
        <span
          style={{
            width: CELL_SIZE,
            height: CELL_SIZE,
            background: scaleColor(0.5),
            boxShadow: `inset 0 0 0 1.5px ${darkTheme.colors.warning}`,
            borderRadius: 2,
          }}
        />
        Power-Session
      </span>
    </div>
  );
}

function SectionCard({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        {hint && <span className="text-[11px] text-text-tertiary">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────

function buildWeeks(daily: DailyHeatmapEntry[]): {
  weeks: HeatmapCell[][];
  totalDays: number;
  activeDays: number;
  totalMinutes: number;
} {
  // Zellen-Berechnung pro Tag
  const cells: HeatmapCell[] = daily.map((d) => {
    const intensityRaw =
      d.plannedMinutes > 0
        ? Math.min(d.completedMinutes / d.plannedMinutes, 1)
        : d.completedMinutes > 0
        ? Math.min(d.completedMinutes / 30, 1) // ohne Plan: 30 min = voll
        : 0;
    return {
      date: d.date,
      intensity: intensityRaw,
      isLearningDay: d.isLearningDay,
      hadPowerSession: d.hadPowerSession,
      completedMinutes: d.completedMinutes,
      plannedMinutes: d.plannedMinutes,
      streakDayNumber: d.streakDayNumber,
    };
  });

  // Padding am Wochenanfang: erster Tag rutscht auf seinen Wochentag.
  if (cells.length === 0) {
    return { weeks: [], totalDays: 0, activeDays: 0, totalMinutes: 0 };
  }
  const first = new Date(cells[0]!.date);
  const firstDayIdx = (first.getUTCDay() + 6) % 7; // Mo=0..So=6
  const padded: HeatmapCell[] = [];
  for (let i = 0; i < firstDayIdx; i++) {
    padded.push({
      date: '',
      intensity: 0,
      isLearningDay: true,
      hadPowerSession: false,
      completedMinutes: 0,
      plannedMinutes: 0,
      streakDayNumber: 0,
      empty: true,
    });
  }
  padded.push(...cells);

  // In 7er-Buckets
  const weeks: HeatmapCell[][] = [];
  for (let i = 0; i < padded.length; i += 7) {
    weeks.push(padded.slice(i, i + 7));
  }

  const totalDays = cells.length;
  const activeDays = cells.filter((c) => c.completedMinutes > 0).length;
  const totalMinutes = cells.reduce((acc, c) => acc + c.completedMinutes, 0);

  return { weeks, totalDays, activeDays, totalMinutes };
}

function cellColor(cell: HeatmapCell): string {
  if (cell.completedMinutes <= 0) {
    return cell.isLearningDay ? darkTheme.colors.bgElevated : darkTheme.colors.bgBase;
  }
  return scaleColor(cell.intensity);
}

// 5-Stufen-Skala in Akzent-Lila — gleiche Logik wie GitHub-Heatmap.
function scaleColor(t: number): string {
  if (t <= 0) return darkTheme.colors.bgElevated;
  if (t < 0.25) return '#3D2D6E';
  if (t < 0.5) return '#5B3FA8';
  if (t < 0.75) return '#7C3AED';
  if (t < 1) return '#9F7AEA';
  return '#C4B5FD';
}

function cellTooltip(cell: HeatmapCell): string {
  const dateStr = new Date(cell.date).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const parts = [dateStr];
  if (cell.completedMinutes > 0) {
    parts.push(`${cell.completedMinutes} min erledigt`);
  } else {
    parts.push('keine Aktivität');
  }
  if (cell.plannedMinutes > 0) parts.push(`${cell.plannedMinutes} min geplant`);
  if (cell.streakDayNumber > 0) parts.push(`Streak-Tag ${cell.streakDayNumber}`);
  if (cell.hadPowerSession) parts.push('Power-Session');
  if (!cell.isLearningDay) parts.push('Lernfreier Tag');
  return parts.join(' · ');
}
